import { useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff, Check, Circle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";
import { PASSWORD_RULES, countPassedRules } from "@/lib/validation/password";

interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange" | "value"> {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  showRules?: boolean;
  error?: string;
}

const STRENGTH_COLORS = ["bg-destructive", "bg-destructive", "bg-yellow-500", "bg-green-600"];

export function PasswordField({
  id,
  label,
  value,
  onChange,
  showRules,
  error,
  className,
  ...rest
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const passed = countPassedRules(value);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn("pr-9", className)}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground"
          aria-label={visible ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
          tabIndex={-1}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>

      {showRules && value.length > 0 && (
        <div className="space-y-1.5 pt-1" aria-live="polite">
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn("h-full transition-all", STRENGTH_COLORS[passed])}
              style={{ width: `${(passed / PASSWORD_RULES.length) * 100}%` }}
            />
          </div>
          <ul className="space-y-0.5 text-xs">
            {PASSWORD_RULES.map((rule) => {
              const ok = rule.test(value);
              return (
                <li
                  key={rule.id}
                  className={cn(
                    "flex items-center gap-1.5",
                    ok ? "text-green-600" : "text-muted-foreground",
                  )}
                >
                  {ok ? <Check size={12} /> : <Circle size={12} />}
                  <span>{rule.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
